import { useCallback, useState } from 'react';
import { toast } from 'sonner';

import api from '@/lib/api';
import { formatApiError } from '@/lib/validators';
import { EMPTY_PAYMENT, ON_ACCOUNT, paymentError, recordCustomerPayment } from '@/lib/payments';

/**
 * Taking a payment for one customer, wherever their name appears.
 *
 * Two screens collect this — the customers list and the customer's profile —
 * and the list has none of the ledger loaded, so the hook fetches what the form
 * needs (what is owed, which bills are open, what advance is already held) when
 * a caller cannot supply it. On a rejection it re-reads that ledger before
 * showing the error, because the usual reason a payment is refused is that
 * another desk collected it first.
 */
export function useRecordPayment({ onRecorded } = {}) {
  const [customer, setCustomer] = useState(null);
  const [context, setContext] = useState({ orders: [], outstanding: 0, advance: 0 });
  const [supplied, setSupplied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY_PAYMENT);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async (customerId) => {
    setLoading(true);
    try {
      const res = await api.get(`/customers/${customerId}/profile`);
      // a city office manager may record a payment without being told the
      // customer's lifetime ledger, so the API withholds the summary — null,
      // which the form reads as "unknown", not as "nothing owed"
      const summary = res.data.financial_summary;
      setContext({
        orders: res.data.outstanding_orders || [],
        outstanding: summary ? summary.outstanding || 0 : null,
        advance: summary?.advance_balance || 0,
      });
    } catch (err) {
      toast.error(formatApiError(err, 'Could not load what this customer owes'));
    }
    setLoading(false);
  }, []);

  const open = useCallback((target, orderId = ON_ACCOUNT, preset = null) => {
    setCustomer(target);
    setForm({ ...EMPTY_PAYMENT, order_id: orderId || ON_ACCOUNT });
    setError(null);
    setSupplied(!!preset);
    if (preset) {
      setContext({
        orders: preset.orders || [],
        outstanding: preset.outstanding ?? null,
        advance: preset.advance || 0,
      });
    } else {
      setContext({ orders: [], outstanding: null, advance: 0 });
      load(target.id);
    }
  }, [load]);

  const close = useCallback(() => {
    setCustomer(null);
    setForm(EMPTY_PAYMENT);
    setError(null);
  }, []);

  const submit = useCallback(async () => {
    if (!customer || submitting) return;
    const invalid = paymentError(form, context.orders);
    if (invalid) {
      setError(invalid);
      return;
    }
    setSubmitting(true);
    try {
      const res = await recordCustomerPayment(customer.id, form);
      toast.success(res.data?.message || 'Payment recorded');
      setCustomer(null);
      setForm(EMPTY_PAYMENT);
      setError(null);
      onRecorded?.(res.data);
    } catch (err) {
      const message = formatApiError(err, 'Failed to record payment');
      setError(message);
      toast.error(message);
      if (!supplied) load(customer.id);
    }
    setSubmitting(false);
  }, [customer, submitting, form, context.orders, onRecorded, supplied, load]);

  const update = useCallback((patch) => {
    setForm((prev) => ({ ...prev, ...(typeof patch === 'function' ? patch(prev) : patch) }));
    setError(null);
  }, []);

  return {
    customer,
    isOpen: !!customer,
    orders: context.orders,
    outstanding: context.outstanding,
    advance: context.advance,
    loading,
    form,
    setForm: update,
    error,
    submitting,
    open,
    close,
    submit,
  };
}

export default useRecordPayment;
